import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { AssetGlyph } from "@/components/market/PriceCell";
import { api } from "@/lib/api";

const KYC = {
  approved: { label: "Verified", tone: "text-up" },
  pending_review: { label: "Under Review", tone: "text-accent" },
  rejected: { label: "Rejected", tone: "text-down" },
  personal_done: { label: "Pending", tone: "text-accent" },
  not_started: { label: "Unverified", tone: "text-accent" },
};

const CTA = {
  pending_review: "View application",
  rejected: "Resubmit KYC",
  personal_done: "Continue KYC",
  not_started: "Start KYC",
};

const inr = (v, d = 0) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: d })}`;
const compactInr = (v) => {
  const n = Number(v || 0);
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return inr(n);
};

function Ring({ value }) {
  const r = 34;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative h-24 w-24 shrink-0">
      <svg viewBox="0 0 80 80" className="h-full w-full -rotate-90">
        <circle cx="40" cy="40" r={r} fill="none" stroke="oklch(var(--surface-2))" strokeWidth="6" />
        <circle
          cx="40"
          cy="40"
          r={r}
          fill="none"
          stroke="oklch(var(--primary))"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - value)}
        />
      </svg>
      <span className="num absolute inset-0 flex items-center justify-center text-sm font-semibold">{Math.round(value * 100)}%</span>
    </div>
  );
}

function useActivity() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let alive = true;
    const pull = () =>
      api
        .get("/api/user/notifications", { params: { limit: 20 } })
        .then(({ data }) => alive && setItems(data?.items || []))
        .catch(() => alive && setItems((x) => x || []));
    pull();
    const id = setInterval(pull, 60000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);
  return items;
}

const dotFor = (type = "") => {
  const t = type.toLowerCase();
  if (/(p2p|trade|order|otc)/.test(t)) return "bg-up";
  if (/(security|login|2fa|session)/.test(t)) return "bg-accent";
  return "bg-primary";
};

function bonusLine(summary) {
  const rows = summary?.bonus_balances || [];
  if (!rows.length) return "Promo codes and welcome bonuses from the admin panel land here.";
  return rows
    .map((b) => {
      const cur = (b.currency || "INR").toUpperCase();
      const n = Number(b.available || 0);
      return cur === "INR" ? `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })} bonus` : `${n} ${cur} bonus`;
    })
    .join(" · ");
}

export default function Dashboard({ tabs = null }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [rewards, setRewards] = useState(null);
  const activity = useActivity();

  useEffect(() => {
    api
      .get("/api/user/overview")
      .then((r) => setData(r.data))
      .catch(() => setFailed(true));
    api
      .get("/api/rewards/summary")
      .then((r) => setRewards(r.data))
      .catch(() => setRewards(false));
  }, []);

  if (!data) {
    return (
      <AppShell>
        {failed ? (
          <p className="py-20 text-center text-sm text-muted-foreground">Could not load account data.</p>
        ) : (
          <div className="space-y-4">
            <div className="skeleton h-8 w-48" />
            <div className="grid gap-4 sm:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="skeleton h-24" />
              ))}
            </div>
          </div>
        )}
      </AppShell>
    );
  }

  const u = data.user;
  const kyc = KYC[u.kyc_status] || KYC.not_started;
  const first = (u.full_name || "").trim().split(" ")[0] || "there";
  const checks = [
    { ok: u.email_verified, todo: "verify your email" },
    { ok: u.mobile_verified, todo: "verify your mobile" },
    { ok: u.totp_enabled, todo: "enable 2FA" },
    { ok: u.kyc_status === "approved", todo: "complete KYC" },
  ];
  const done = checks.filter((c) => c.ok).length;
  const pending = checks.filter((c) => !c.ok).map((c) => c.todo);
  const todoLine = pending.length
    ? `${pending.join(", ").replace(/^./, (s) => s.toUpperCase()).replace(/, ([^,]*)$/, " and $1")} to reach 100%.`
    : "Your account is fully set up.";

  return (
    <AppShell>
      <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Hello, {first}</h1>
            <p className="mt-1 text-sm text-muted-foreground">Here&apos;s where your account stands today.</p>
          </div>
          {tabs}
        </div>

        {u.trading_suspended && (
          <div className="rounded-xl border border-down/40 bg-down/10 px-5 py-4 text-sm text-down" data-testid="suspended-banner">
            <span className="font-medium">Trading suspended.</span>{" "}
            {u.suspended_reason || "Your trading access is temporarily on hold due to risk or compliance rules."}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-border bg-surface/50 p-5" data-testid="kyc-banner">
            <p className="text-xs text-muted-foreground">KYC status</p>
            <p className={`num mt-2 text-lg font-semibold ${kyc.tone}`}>{kyc.label}</p>
          </div>
          <div className="rounded-xl border border-border bg-surface/50 p-5">
            <p className="text-xs text-muted-foreground">Account tier</p>
            <p className="num mt-2 text-lg font-semibold">{u.kyc_tier || "Tier 1"}</p>
          </div>
          <div className="rounded-xl border border-border bg-surface/50 p-5" data-testid="dash-total-balance">
            <p className="text-xs text-muted-foreground">Portfolio value</p>
            <p className="num mt-2 text-lg font-semibold">{inr(data.total_value_inr, 2)}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-surface/50 px-5 py-4" data-testid="dash-rewards">
          <div>
            <p className="text-sm font-medium">Rewards & bonuses</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {rewards && rewards !== false
                ? bonusLine(rewards)
                : "Promo codes and welcome bonuses from the admin panel land here."}
              {rewards?.pending_kyc ? ` · ${rewards.pending_kyc} waiting on KYC` : ""}
            </p>
          </div>
          <Link to="/profile/rewards" className="brand-gradient rounded-lg px-4 py-2 text-sm font-semibold text-primary-foreground">
            View rewards
          </Link>
        </div>

        {u.kyc_status !== "approved" && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/5 px-5 py-4">
            <div>
              <p className="text-sm font-medium">
                {u.kyc_status === "rejected" ? "Your KYC needs another look" : "Complete KYC to unlock higher limits"}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {u.kyc_status === "rejected" && u.kyc_rejection_reason
                  ? u.kyc_rejection_reason
                  : "Aadhaar OTP · PAN · face match · bank account — verified in minutes."}
              </p>
            </div>
            <Link to="/profile/kyc" className="brand-gradient rounded-lg px-4 py-2 text-sm font-semibold text-primary-foreground">
              {CTA[u.kyc_status] || "Start KYC"}
            </Link>
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-[1fr_1.3fr]">
          <div className="flex items-center gap-5 rounded-xl border border-border bg-surface/50 p-5">
            <Ring value={done / checks.length} />
            <div>
              <p className="text-sm font-medium">Profile completion</p>
              <p className="mt-1 text-xs text-muted-foreground">{todoLine}</p>
              <div className="num mt-3 flex gap-4 text-xs text-muted-foreground">
                <span>{data.p2p_trades || 0} trades</span>
                <span>{compactInr(data.p2p_volume_inr)} volume</span>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-surface/50">
            <p className="border-b border-border px-5 py-3 text-sm font-medium">Live activity</p>
            <div className="max-h-72 space-y-1 overflow-y-auto p-2">
              {activity === null ? (
                <div className="space-y-2 p-3">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="skeleton h-10 w-full" />
                  ))}
                </div>
              ) : activity.length === 0 ? (
                <p className="px-3 py-8 text-center text-xs text-muted-foreground">No activity yet. Trades, deposits and security events show up here.</p>
              ) : (
                activity.map((n) => (
                  <div key={n.notification_id} className="flex items-start gap-3 rounded-lg px-3 py-2 hover:bg-surface-2">
                    <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${dotFor(n.type)}`} />
                    <div className="min-w-0">
                      <p className="text-sm">{n.title}</p>
                      <p className="text-xs text-muted-foreground">{n.message}</p>
                    </div>
                    <span className="num ml-auto shrink-0 text-[10px] text-muted-foreground">
                      {new Date(n.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {(data.assets || []).length > 0 && (
          <div data-testid="dash-portfolio">
            <div className="flex items-end justify-between">
              <h2 className="text-lg font-semibold">Holdings</h2>
              <Link to="/wallets" className="text-xs text-primary hover:underline">
                Open wallets
              </Link>
            </div>
            <div className="mt-3 overflow-x-auto rounded-xl border border-border bg-surface/40">
              <div className="min-w-[560px]">
                <div className="table-head grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-3 px-4 py-2.5">
                  <span>Asset</span>
                  <span className="text-right">Balance</span>
                  <span className="text-right">USD</span>
                  <span className="text-right">INR</span>
                </div>
                {data.assets.map((a) => (
                  <div key={a.symbol} className="terminal-row grid-cols-[1.4fr_1fr_1fr_1fr] gap-3 px-4 py-3 text-sm">
                    <span className="flex items-center gap-3">
                      <AssetGlyph symbol={a.symbol} image={a.image} size={26} />
                      <span>
                        {a.name || a.symbol} <span className="text-xs text-muted-foreground">{a.symbol}</span>
                      </span>
                    </span>
                    <span className="num text-right">{Number(a.balance).toLocaleString("en-US", { maximumFractionDigits: 8 })}</span>
                    <span className="num text-right text-muted-foreground">
                      ${(Number(a.balance) * Number(a.price_usd || 0)).toLocaleString("en-US", { maximumFractionDigits: 2 })}
                    </span>
                    <span className="num text-right">{inr(a.value_inr)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
